import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { Button, cn } from "@city-sage/aurum-avis-labs-components";
import { useComments } from "../../lib/useComments";
import { PostPostableTypeEnum } from "@city-sage/core-client";
import { CreatePostSheet } from "./CreatePostSheet";

interface CommentButtonProps {
  postableType: 'itinerary' | 'stop';
  postableId: string;
  onClick?: () => void;
  variant?: 'light' | 'dark';
  layout?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
}

export const CommentButton = ({
  postableType,
  postableId,
  onClick,
  variant = 'light',
  layout = 'horizontal',
  size = 'md'
}: CommentButtonProps) => {
  const { commentCount, isLoading } = useComments({ postableType, postableId });
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);

  // Convert postableType to enum
  const postableTypeEnum = postableType === 'itinerary' 
    ? PostPostableTypeEnum.Itinerary 
    : PostPostableTypeEnum.Stop;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default behavior: open the create post sheet
      setIsCreatePostOpen(true);
    }
  };
  const buttonSizes = {
    sm: "h-8 w-8",
    md: "h-10 w-10", 
    lg: "h-12 w-12"
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  };

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-sm"
  };

  const buttonClasses = cn(
    "rounded-full transition-all duration-200",
    buttonSizes[size],
    variant === 'dark' ? [
      "backdrop-blur-md bg-black/30 text-white hover:bg-white/20"
    ] : [
      "bg-gray-100 text-gray-600 hover:bg-gray-200"
    ]
  );

  const countClasses = cn(
    "font-medium",
    textSizes[size],
    variant === 'dark' ? "text-white/90" : "text-gray-600"
  );

  // Generate placeholder text based on postableType
  const placeholderText = postableType === 'itinerary' 
    ? "Share your thoughts about this itinerary..." 
    : "Comment on this stop...";

  if (layout === 'vertical') {
    return (
      <>
        <div className="text-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClick}
            disabled={isLoading}
            className={buttonClasses}
          >
            <MessageCircle className={iconSizes[size]} />
          </Button>
          <div className={cn(countClasses, "mt-1")}>
            {isLoading ? "..." : commentCount}
          </div>
        </div>
        
        {/* Create Post Sheet */}
        <CreatePostSheet
          isOpen={isCreatePostOpen}
          onClose={() => setIsCreatePostOpen(false)}
          postableType={postableTypeEnum}
          postableId={postableId}
          placeholder={placeholderText}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          disabled={isLoading}
          className={buttonClasses}
        >
          <MessageCircle className={iconSizes[size]} />
        </Button>
        <span className={countClasses}>
          {isLoading ? "..." : commentCount}
        </span>
      </div>
      
      {/* Create Post Sheet */}
      <CreatePostSheet
        isOpen={isCreatePostOpen}
        onClose={() => setIsCreatePostOpen(false)}
        postableType={postableTypeEnum}
        postableId={postableId}
        placeholder={placeholderText}
      />
    </>
  );
}; 